import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { isEqual } from 'lodash-es';
import type { IFilterTopicResponse } from '@op/services';

// ============ 常量 ============
/** category 和 subcategory 合并使用的特殊符号 */
export const CATEGORY_SEPARATOR = '┠┨';

// ============ 类型定义 ============
interface RegionSelectOption {
  value: string;
  text: string;
  textCN: string;
  _source?: string;
  children?: RegionSelectOption[];
}

interface UseCategoryTabOptions {
  hasCategoryTab: boolean; // 是否启用分类 Tab
  categoryOptions?: IFilterTopicResponse; // 分类数据源
  defaultValue?: {
    categories?: Array<{
      category: string;
      sub_categories: string[];
    }>; // 默认值（回显数据格式）
  };
  filterValue?: {
    categories?: Array<{
      category: string;
      sub_categories: string[];
    }>;
    topicType?: string;
  };
}

interface CategoryValue {
  markets: string[]; // 二级分类选中值（格式：category┠┨subcategory）
  regions: string[]; // 一级分类选中值（categories）
  type: 'market'; // 类型（固定 market）
}

interface UseCategoryTabReturn {
  // 状态
  categoryValue: CategoryValue; // 分类选中值（NewRegion 格式）

  // 计算值
  categoryOptions: RegionSelectOption[]; // 转换后的 options
  isAll: boolean; // 是否全选
  selectedCount: number; // 选中数量（全选时为 'All' 的数量，否则为实际选中数）

  // 方法
  getCategoryValue: () => any; // 获取回调值（返回回显数据格式）
  handleCategoryChange: (result: CategoryValue) => void; // 处理选择变化

  // 工具方法
  resetCategoryState: () => void; // 重置状态
}

// ============ 回显数据类型 ============
/** 回显数据格式 */
interface EchoCategoryItem {
  category: string;
  sub_categories: string[];
}

interface EchoData {
  categories?: EchoCategoryItem[];
}

// ============ 全选判断 ============
/**
 * 判断是否全选
 * 全选有两种情况：
 * 1. categories: [{ category: "99999", sub_categories: [] }]
 * 2. categories: []（空数组）
 */
function isAllSelected(categories?: EchoCategoryItem[]): boolean {
  if (!categories || categories.length === 0) return true;
  return (
    categories.length === 1 &&
    categories[0].category === '99999' &&
    categories[0].sub_categories.length === 0
  );
}

// ============ 数据合并/拆分方法 ============
/**
 * 合并 category 和 subcategory 为 markets 格式
 * @param category 一级分类（小写）
 * @param subCategory 二级分类（小写）
 * @returns 合并后的值，格式：category┠┨subcategory
 */
export function mergeCategoryValue(
  category: string,
  subCategory: string,
): string {
  return `${category}${CATEGORY_SEPARATOR}${subCategory}`;
}

/**
 * 拆分 markets 格式为 category 和 subcategory
 * @param value 格式：category┠┨subcategory
 * @returns { category, subCategory }
 */
export function splitCategoryValue(value: string): {
  category: string;
  subCategory: string;
} {
  const [category, subCategory] = value.split(CATEGORY_SEPARATOR);
  return { category, subCategory };
}

// ============ 数据转换方法 ============
/**
 * 将回显数据格式转换为过滤器数据格式
 * 回显数据 → { markets, regions, type: 'market' }
 */
export function echoToFilterData(
  echoData: EchoData,
  categoryOptions: RegionSelectOption[],
): CategoryValue {
  // 判断全选
  if (isAllSelected(echoData?.categories)) {
    return {
      markets: ['global'],
      regions: [],
      type: 'market',
    };
  }

  const markets: string[] = [];
  const regionsSet = new Set<string>();
  echoData.categories?.forEach?.(item => {
    const category = item.category.toLowerCase();
    regionsSet.add(category);

    const categoryOption = categoryOptions.find(opt => opt.value === category);
    if (item.sub_categories.length === 0) {
      // 一级全选：添加该 category 下所有子项
      categoryOption?.children?.forEach(child => {
        markets.push(child.value);
      });
    } else {
      // 部分选中：只添加指定的 sub_categories
      item.sub_categories.forEach(sub => {
        markets.push(mergeCategoryValue(category, sub.toLowerCase()));
      });
    }
  });

  return {
    markets,
    regions: Array.from(regionsSet),
    type: 'market',
  };
}

/**
 * 将过滤器数据格式转换为回显数据格式
 * { markets, regions, type: 'market' } → 回显数据
 */
export function filterDataToEcho(
  filterValue: CategoryValue,
  categoryOptions: RegionSelectOption[],
): EchoData {
  const { markets, regions } = filterValue;

  // 全选判断
  if (markets.length === 1 && markets[0] === 'global') {
    return {
      categories: [{ category: '99999', sub_categories: [] }],
    };
  }

  // 按 category 分组 markets
  const categoryMap = new Map<string, string[]>();

  markets.forEach(market => {
    const { category, subCategory } = splitCategoryValue(market);
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category)!.push(subCategory);
  });

  // 构建回显数据
  const categories: EchoCategoryItem[] = [];

  categoryMap.forEach((subCategories, category) => {
    const categoryOption = categoryOptions.find(opt => opt.value === category);
    const allSubValues = categoryOption?.children?.map(c => c.value) || [];

    // 判断是否该 category 全选
    if (subCategories.length === allSubValues.length) {
      categories.push({
        category,
        sub_categories: [],
      });
    } else {
      categories.push({
        category,
        sub_categories: subCategories,
      });
    }
  });

  return { categories };
}

export const useCategoryTab = (
  options: UseCategoryTabOptions,
): UseCategoryTabReturn => {
  const {
    hasCategoryTab,
    categoryOptions: categoryData,
    defaultValue = {},
    filterValue = {},
  } = options;

  // ============ 数据转换 ============
  const categoryOptions = useMemo<RegionSelectOption[]>(() => {
    if (!hasCategoryTab || !categoryData?.categories?.length) return [];

    return categoryData.categories.map(category => {
      const categoryValue = category.category_name_en.toLowerCase();
      return {
        value: categoryValue,
        text: category.category_name_en,
        textCN: category.category_name,
        _source: 'category',
        children: category.subcategories.map(sub => {
          const subValue = sub.sub_category_name_en.toLowerCase();
          return {
            value: mergeCategoryValue(categoryValue, subValue), // 合并格式
            text: sub.sub_category_name_en,
            textCN: sub.sub_category_name,
          };
        }),
      };
    });
  }, [categoryData]);

  // ============ 状态 ============
  const defaultCategoryValue: CategoryValue = useMemo(() => {
    // 将回显格式的 defaultValue 转换为过滤器格式
    return echoToFilterData(defaultValue, categoryOptions);
  }, [defaultValue, categoryOptions]);

  const [categoryValue, setCategoryValue] =
    useState<CategoryValue>(defaultCategoryValue);

  // ============ 状态同步 ============
  // 优先级：filterValue（回显） > defaultValue（初始化） > 全选默认值
  // 使用 ref + isEqual 跟踪上一次的值，避免非 memoized 的值导致反复触发
  const prevFilterValueRef = useRef<EchoData['categories']>();
  const prevDefaultValueRef = useRef<EchoData['categories']>();

  useEffect(() => {
    if (!hasCategoryTab || !categoryOptions.length) return;

    // 1. 优先：filterValue 回显数据（由外部控制变化）
    if (filterValue?.categories) {
      if (!isEqual(prevFilterValueRef.current, filterValue.categories)) {
        prevFilterValueRef.current = filterValue.categories;
        const converted = echoToFilterData(filterValue, categoryOptions);
        setCategoryValue(converted);
      }
      return;
    }

    // 2. 其次：defaultValue 初始化数据（只在首次或内容变化时同步）
    if (defaultValue?.categories) {
      if (!isEqual(prevDefaultValueRef.current, defaultValue.categories)) {
        prevDefaultValueRef.current = defaultValue.categories;
        const converted = echoToFilterData(defaultValue, categoryOptions);
        setCategoryValue(converted);
      }
      return;
    }

    // 3. 最后：全选默认值
    setCategoryValue({
      markets: ['global'],
      regions: [],
      type: 'market',
    });
  }, [filterValue, defaultValue, categoryOptions, hasCategoryTab]);

  // ============ 计算值 ============
  // 计算所有子选项数量
  const allSubCategoriesCount = useMemo(() => {
    return categoryOptions.flatMap(
      item => item.children?.map(c => c.value) || [],
    ).length;
  }, [categoryOptions]);

  // 判断是否全选
  const isAll = useMemo(() => {
    return (
      categoryValue.markets.length === 1 &&
      categoryValue.markets[0] === 'global'
    );
  }, [categoryValue.markets]);

  // 选中数量
  const selectedCount = useMemo(() => {
    if (isAll) return allSubCategoriesCount;
    return categoryValue.markets.length;
  }, [isAll, categoryValue.markets.length, allSubCategoriesCount]);

  // ============ 工具方法 ============
  /**
   * 获取回调值（返回回显数据格式）
   * 回显数据 ---进入过滤器---> 转为过滤器需要的格式 ---过滤器修改---> 返回回显数据格式
   */
  const getCategoryValue = useCallback((): EchoData => {
    return filterDataToEcho(categoryValue, categoryOptions);
  }, [categoryValue, categoryOptions]);

  const handleCategoryChange = useCallback((result: CategoryValue) => {
    setCategoryValue(result);
  }, []);

  const resetCategoryState = useCallback(() => {
    setCategoryValue({
      markets: ['global'],
      regions: [],
      type: 'market',
    });
  }, []);

  return {
    // 状态
    categoryValue,

    // 计算值
    categoryOptions,
    isAll,
    selectedCount,

    // 方法
    getCategoryValue,
    handleCategoryChange,
    resetCategoryState,
  };
};
